import React, { useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Placeholder from '@tiptap/extension-placeholder';
import { cleanWordHtml } from '../utils/pasteCleanup';

const ToolbarButton = ({ onClick, active, disabled, title, children }) => (
    <button
        type="button"
        className={`btn btn-sm ${active ? 'btn-primary' : 'btn-outline-secondary'}`}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onClick}
        disabled={disabled}
        title={title}
    >
        {children}
    </button>
);

const ToolbarDivider = () => <span className="rich-text-toolbar-divider" aria-hidden="true" />;

/**
 * Dual-mode (visual / HTML source) rich text editor for the email composer,
 * built on TipTap. `value` is always the current HTML string; `onChange`
 * fires with the updated HTML whenever either mode changes the content.
 */
const RichTextEditor = ({ value, onChange, placeholder = 'Write your email...' }) => {
    const [mode, setMode] = useState('visual'); // 'visual' | 'source'
    const [sourceDraft, setSourceDraft] = useState(value || '');

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: { levels: [1, 2, 3] },
            }),
            Underline,
            Link.configure({ openOnClick: false, autolink: true }),
            Image,
            Table.configure({ resizable: true }),
            TableRow,
            TableHeader,
            TableCell,
            Placeholder.configure({ placeholder }),
        ],
        content: value || '',
        onUpdate: ({ editor: e }) => {
            onChange(e.getHTML());
        },
        editorProps: {
            transformPastedHTML: (html) => cleanWordHtml(html),
        },
    });

    // Keep the visual editor in sync if `value` changes externally (e.g. template applied).
    useEffect(() => {
        if (editor && mode === 'visual' && value !== editor.getHTML()) {
            editor.commands.setContent(value || '', false);
        }
    }, [value]);

    const switchToSource = () => {
        setSourceDraft(editor ? editor.getHTML() : value || '');
        setMode('source');
    };

    const switchToVisual = () => {
        onChange(sourceDraft);
        if (editor) editor.commands.setContent(sourceDraft || '', false);
        setMode('visual');
    };

    const addLink = () => {
        const url = window.prompt('Link URL');
        if (!url) return;
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    };

    const addImage = () => {
        const url = window.prompt('Image URL');
        if (!url) return;
        editor.chain().focus().setImage({ src: url }).run();
    };

    const addTable = () => {
        editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
    };

    if (!editor) return null;

    return (
        <div className="rich-text-editor">
            <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="btn-group" role="group" aria-label="Editor mode">
                    <button
                        type="button"
                        className={`btn btn-sm ${mode === 'visual' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={switchToVisual}
                    >
                        <i className="bi bi-eye me-1"></i> Visual
                    </button>
                    <button
                        type="button"
                        className={`btn btn-sm ${mode === 'source' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={switchToSource}
                    >
                        <i className="bi bi-code-slash me-1"></i> HTML Source
                    </button>
                </div>
            </div>

            {mode === 'visual' ? (
                <>
                    <div className="rich-text-toolbar" role="toolbar" aria-label="Formatting">
                        <ToolbarButton title="Bold (Ctrl+B)" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
                            <i className="bi bi-type-bold"></i>
                        </ToolbarButton>
                        <ToolbarButton title="Italic (Ctrl+I)" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>
                            <i className="bi bi-type-italic"></i>
                        </ToolbarButton>
                        <ToolbarButton title="Underline (Ctrl+U)" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}>
                            <i className="bi bi-type-underline"></i>
                        </ToolbarButton>

                        <ToolbarDivider />

                        <ToolbarButton title="Bullet list" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>
                            <i className="bi bi-list-ul"></i>
                        </ToolbarButton>
                        <ToolbarButton title="Numbered list" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
                            <i className="bi bi-list-ol"></i>
                        </ToolbarButton>

                        <ToolbarDivider />

                        <ToolbarButton title="Insert link" active={editor.isActive('link')} onClick={addLink}>
                            <i className="bi bi-link-45deg"></i>
                        </ToolbarButton>
                        <ToolbarButton title="Insert image" onClick={addImage}>
                            <i className="bi bi-image"></i>
                        </ToolbarButton>
                        <ToolbarButton title="Insert table" onClick={addTable}>
                            <i className="bi bi-table"></i>
                        </ToolbarButton>
                        <ToolbarButton title="Code block" active={editor.isActive('codeBlock')} onClick={() => editor.chain().focus().toggleCodeBlock().run()}>
                            <i className="bi bi-code-square"></i>
                        </ToolbarButton>
                        <ToolbarButton title="Horizontal rule" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
                            <i className="bi bi-hr"></i>
                        </ToolbarButton>

                        <ToolbarDivider />

                        <ToolbarButton title="Undo (Ctrl+Z)" onClick={() => editor.chain().focus().undo().run()}>
                            <i className="bi bi-arrow-counterclockwise"></i>
                        </ToolbarButton>
                        <ToolbarButton title="Redo (Ctrl+Shift+Z)" onClick={() => editor.chain().focus().redo().run()}>
                            <i className="bi bi-arrow-clockwise"></i>
                        </ToolbarButton>
                    </div>
                    <div
                        className="rich-text-content-area"
                        style={{ border: '1px solid #cbd5e1', borderRadius: '6px', minHeight: '320px', padding: '12px', background: '#fff' }}
                    >
                        <EditorContent editor={editor} />
                    </div>
                </>
            ) : (
                <textarea
                    className="form-control"
                    rows={16}
                    style={{ fontFamily: 'monospace', fontSize: '13px' }}
                    value={sourceDraft}
                    onChange={(e) => setSourceDraft(e.target.value)}
                    onBlur={() => onChange(sourceDraft)}
                />
            )}
        </div>
    );
};

export default RichTextEditor;
